import { Flag, Pencil } from "lucide-react";
import { AppLink, formatDateTime } from "@amber/ui";
import type { EvidenceRecord, ProblemView, TurnView } from "../../contracts/index";
import { CarryNotes } from "../copies/CarryNotes";

function CorrectionNote({ turn }: { turn: TurnView }) {
  const corrected = turn.corrected;
  const words = turn.words.filter((w) => w.corrected);
  if (corrected) {
    return (
      <p className="m-0 mt-1 flex items-center gap-1.5 text-md text-muted">
        <Pencil size={12} aria-hidden="true" />
        {corrected.from
          ? `Carried over from ${corrected.from} by ${corrected.by}`
          : `Corrected by ${corrected.by}`} · {formatDateTime(corrected.at)} · checks rerun
      </p>
    );
  }
  if (!words[0]?.corrected) return null;
  return (
    <p className="m-0 mt-1 flex items-center gap-1.5 text-md text-muted">
      <Pencil size={12} aria-hidden="true" />
      {words.length} word{words.length === 1 ? "" : "s"} corrected by {words[0].corrected.by} ·{" "}
      {formatDateTime(words[0].corrected.at)}
    </p>
  );
}

export function TurnNotes({
  record,
  turn,
  canEdit,
  onResolve,
}: {
  record: EvidenceRecord;
  turn: TurnView;
  canEdit: boolean;
  onResolve: (p: ProblemView) => void;
}) {
  const problems = record.problems.filter((p) => turn.problems.includes(p.id));
  return (
    <>
      <CorrectionNote turn={turn} />
      {turn.confirmed ? (
        <p className="m-0 mt-1 text-md text-muted">
          Speaker confirmed by {turn.confirmed.by} · {formatDateTime(turn.confirmed.at)}
        </p>
      ) : null}
      <CarryNotes itemId={record.item.id} notes={turn.carry_notes} canEdit={canEdit && record.pinned === null} />
      {problems.map((p) => (
        <div key={p.id} className="mt-1.5 flex items-start gap-1.5 rounded-md bg-surface px-2 py-1.5 text-md">
          <Flag size={12} aria-hidden="true" className="mt-[3px] flex-none text-attention" />
          <div className="min-w-0 flex-1">
            <span className="font-strong">Problem reported · {p.kind_label}</span>
            <span className="text-muted">
              {" "}
              · {p.reported_by}, {formatDateTime(p.reported_at)}
            </span>
            <p className="m-0 text-muted">{p.description}</p>
          </div>
          <span className="flex flex-none items-center gap-2">
            <button
              type="button"
              disabled={!canEdit}
              title={canEdit ? undefined : "Only Editors and above can resolve reports"}
              onClick={() => {
                onResolve(p);
              }}
              className="min-h-6 text-link disabled:cursor-not-allowed disabled:text-muted"
            >
              Mark resolved…
            </button>
            {p.task_href ? (
              <AppLink href={p.task_href} className="text-link">
                Open task
              </AppLink>
            ) : null}
          </span>
        </div>
      ))}
    </>
  );
}
